import { MODULE } from '../services/permission.service';

/**
 * Maps a permission module (access_level_tb.name) to its i18n key.
 *
 * The map is keyed off the MODULE constants in permission.service.ts - the
 * single source of truth for the DB module names - and the `satisfies`
 * constraint makes adding a module to MODULE a compile error here until a
 * display key is chosen. Each module has its own `access_module_*` key
 * (pt/pt-BR values pinned to the DB names, so the PT experience does not
 * change) rather than borrowing sidebar keys, so navigation copy edits can
 * never silently rename permission-matrix rows. Lookup is lowercased like
 * the sibling utils, guarding against casing/accent drift in seeded data.
 * Permission LOGIC keeps comparing the raw DB names - this is display only.
 */
export const ACCESS_MODULE_KEYS = {
  [MODULE.DASHBOARD]: 'access_module_dashboard',
  [MODULE.AGENDA]: 'access_module_agenda',
  [MODULE.CRM]: 'access_module_crm',
  [MODULE.FINANCEIRO]: 'access_module_financial',
  [MODULE.FORMULARIOS]: 'access_module_forms',
  [MODULE.LABORATORIOS]: 'access_module_laboratories',
  [MODULE.PACIENTES]: 'access_module_patients',
  [MODULE.PROCEDIMENTOS]: 'access_module_procedures',
  [MODULE.PROFISSIONAIS]: 'access_module_professionals',
  [MODULE.SALAS]: 'access_module_rooms',
  [MODULE.CONFIGURACOES]: 'access_module_settings',
  [MODULE.CONSULTAS]: 'access_module_consultations',
  [MODULE.CONSULTAS_ADMIN]: 'access_module_consultations_admin',
  [MODULE.FEEDBACK]: 'access_module_feedback',
  [MODULE.FEEDBACK_ALL]: 'access_module_feedback_all',
  [MODULE.FEEDBACK_NOTES]: 'access_module_feedback_notes',
  [MODULE.COMPLAINTS]: 'access_module_complaints',
} as const satisfies Record<(typeof MODULE)[keyof typeof MODULE], string>;

const ACCESS_MODULE_KEYS_LOWER: Record<string, string> = {
  ...Object.fromEntries(
    Object.entries(ACCESS_MODULE_KEYS).map(([name, key]) => [name.toLowerCase(), key])
  ),
  // unaccented aliases, same guard as the sibling utils' 'urgencia'/'concluido'
  formularios: 'access_module_forms',
  laboratorios: 'access_module_laboratories',
  configuracoes: 'access_module_settings',
};

/**
 * Resolves the value to feed `| translate` for a permission module name.
 * Returns the i18n key for a known module, else the raw name (which passes
 * through ngx-translate unchanged, so an unexpected module still renders).
 */
export function accessModuleKey(moduleName: string | null | undefined): string {
  const name = (moduleName ?? '').trim();
  if (!name) return '';
  return ACCESS_MODULE_KEYS_LOWER[name.toLowerCase()] ?? name;
}
